
import React, { useEffect, useRef, useMemo, useState } from "react";
import { Table, TableBody } from "@/components/ui/table";
import { Account, LeadAccount, StrategyWithDetails } from "@/types/strategy-types";
import { useFollowLinksCount } from "@/hooks/admin/accounts/useFollowLinksCount";
import AccountsTableHeader from "./table/AccountsTableHeader";
import AccountTableRow from "./table/AccountTableRow";

interface AccountsDataTableProps {
  accounts: Account[];
  leadAccounts: LeadAccount[];
  strategies: StrategyWithDetails[];
  onToggleActive: (id: string, active: boolean) => Promise<void>;
  onEdit?: (account: Account) => void;
  onDelete?: (account: Account) => void;
  userRole?: string | null;
}

const AccountsDataTable = ({
  accounts,
  leadAccounts,
  strategies,
  onToggleActive,
  onEdit,
  onDelete,
  userRole
}: AccountsDataTableProps) => {
  const accountIds = useMemo(() => accounts.map(account => account.id), [accounts]);
  const initialRenderComplete = useRef(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const previousAccountIds = useRef<string[]>([]);
  
  console.log("AccountsDataTable rendering with accountIds:", accountIds);
  
  const { linkCounts, isLoading, error, refetch, clearCache } = useFollowLinksCount(accountIds);
  
  useEffect(() => {
    initialRenderComplete.current = true;
  }, []);
  
  useEffect(() => {
    if (previousAccountIds.current.length > 0) {
      const removedAccountIds = previousAccountIds.current.filter(
        id => !accountIds.includes(id)
      );
      
      if (removedAccountIds.length > 0) {
        console.log(`Accounts removed: ${removedAccountIds.join(', ')}`);
        clearCache(removedAccountIds);
      }
    }
    
    previousAccountIds.current = [...accountIds];
  }, [accountIds, clearCache]);
  
  useEffect(() => {
    const handleGlobalRefresh = () => {
      console.log("Global refresh event received in AccountsDataTable");
      setRefreshKey(prev => prev + 1);
      refetch();
    };
    
    window.addEventListener('refresh-link-counts', handleGlobalRefresh);
    
    return () => {
      window.removeEventListener('refresh-link-counts', handleGlobalRefresh);
    };
  }, [refetch]);

  const getLeadAccountName = (leadAccountId: string) => {
    const leadAccount = leadAccounts.find(la => la.id === leadAccountId);
    return leadAccount ? leadAccount.external_id : 'Unknown';
  };

  const getStrategyName = (strategyId: string) => {
    const strategy = strategies.find(s => s.id === strategyId);
    return strategy ? strategy.name : 'Unknown';
  };

  const handleEdit = (account: Account) => {
    if (onEdit) {
      onEdit(account);
    }
  };

  const handleDelete = (account: Account) => {
    if (onDelete) {
      onDelete(account);
    }
  };

  const handleManualRefresh = () => {
    console.log("Manual refresh requested");
    refetch();
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <AccountsTableHeader onManualRefresh={handleManualRefresh} />
        <TableBody>
          {accounts.map((account) => (
            <AccountTableRow
              key={`${account.id}-${refreshKey}`}
              account={account}
              getLeadAccountName={getLeadAccountName}
              getStrategyName={getStrategyName}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={onToggleActive}
              userRole={userRole}
              refreshKey={refreshKey}
              linkCounts={linkCounts}
              isLoading={isLoading}
              error={error}
              onManualRefresh={handleManualRefresh}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default AccountsDataTable;
